import { useQuery } from '@apollo/client';
import toUpper from 'lodash/toUpper';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import AddIcon from '@atlaskit/icon/glyph/add';
import VerticalMoreIcon from '@atlaskit/icon/glyph/more-vertical';
import {
  DynamicResultTable,
  FormattedDate,
  NoDataContentInTable,
  OpenSidebarButton,
  OpenSidebarPayload,
  useFeatureToggle,
  useSidebar,
} from '@css-xp/base';
import { TransactionAccountContext } from '@css-xp/base/common-context';
import { EntitlementStatus } from '@css-xp/base/entitlement-utils';
import {
  AddOn,
  AddOnConnection,
  FetchAddOnsForEntitlementQuery as AddOnsQuery,
  FetchAddOnsForEntitlementQueryVariables as AddOnsQueryVariables,
  EntitlementDetails,
  Maybe,
  SortDirection,
} from '@css-xp/graphtypes/src/generated-types-new';
import { STATUSPAGE_ADDONS_PATH_2 } from '@atlassian/cssxp-typescommon';
import {
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
  NO_DETAILS_TO_SHOW,
  isDeletedEntitlement,
} from '@atlassian/cssxp-utils-entitlement';
import { ENABLE_SANDBOX_ENTITLEMENT } from '@atlassian/cssxp-utils-generic';
import { SIDEBAR } from '../../..';
import { PREDICATES } from '../../../../common/constants';
import { getPredicates } from '../../../../common/utils';
import { fetchAddOnsForEntitlement } from './queries/fetch-add-ons-for-entitlement';

export interface AddOnsTabProps {
  entitlement: EntitlementDetails;
}

const BillingStatus = {
  ACTIVE: 'ACTIVE',
  DELETED: 'DELETED',
  INACTIVE: 'INACTIVE',
  SUSPENDED: 'SUSPENDED',
};

const defaultSearchOptions: AddOnsQueryVariables['searchOptions'] = {
  first: DEFAULT_ROWS_PER_PAGE,
  offset: 0,
  sort: {
    sortFields: ['createdDate', 'id'],
    sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC],
  },
};
export const emptyStateOrderProp = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
  imageUrl: '',
  testId: 'no-results-addon-state',
};

const AddOnsTab = ({ entitlement }: AddOnsTabProps) => {
  const id = entitlement.id;
  const { refetch, data, loading } = useQuery<AddOnsQuery, AddOnsQueryVariables>(fetchAddOnsForEntitlement, {
    variables: { id: id, searchOptions: defaultSearchOptions },
    displayName: 'fetchAddOns',
    notifyOnNetworkStatusChange: true,
  });
  const transactionAccountContext = React.useContext(TransactionAccountContext);
  const { openSidebar } = useSidebar();
  const [isEnabled] = useFeatureToggle();

  const isSandbox = useMemo(() => {
    return isEnabled(ENABLE_SANDBOX_ENTITLEMENT) && entitlement?.isSandbox;
  }, [isEnabled, entitlement]);

  const onPageChange = (newPage: number) => {
    if (currentPage === newPage) {
      return;
    }
    setCurrentPage(newPage);
    refetch({
      id: id,
      searchOptions: {
        ...defaultSearchOptions,
        offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
        first: rowsPerPage,
      },
    });
  };

  const onRowPerPageChange = (newRowPerPage: number) => {
    if (newRowPerPage === rowsPerPage) {
      return;
    }
    setCurrentPage(1);
    setRowPerPage(newRowPerPage);
    refetch({
      id: id,
      searchOptions: {
        ...defaultSearchOptions,
        offset: 0,
        first: newRowPerPage,
      },
    });
  };

  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const queryResults = data?.entitlementDetails.addOns as AddOnConnection;
  const totalCount = queryResults?.totalCount || 0;
  const addOnEdges = queryResults?.edges;
  const showPagination = totalCount > DEFAULT_ROWS_PER_PAGE;

  const getAddonLink = (addOn?: Maybe<AddOn>) => {
    return `${STATUSPAGE_ADDONS_PATH_2}/${addOn?.entitlementNumber}`;
  };

  const getEntitlementRow = (addOn?: Maybe<AddOn>) => {
    if (addOn?.entitlementNumber) {
      return (
        <div id="entitlement-number">
          <Link
            to={getAddonLink(addOn)}
            onClick={(e) => e.stopPropagation()}
            className="avatar-container"
            data-testid={addOn?.entitlementNumber}
          >
            {addOn?.entitlementNumber}
          </Link>
        </div>
      );
    }
    return <NoDataContentInTable />;
  };

  const getNextBillingState = (addOn?: Maybe<AddOn>): React.ReactElement => {
    return addOn?.billingStatus && toUpper(addOn.billingStatus) === BillingStatus.ACTIVE ? (
      <FormattedDate date={addOn.nextBillDate} />
    ) : (
      <NoDataContentInTable />
    );
  };

  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };
  const getAddPromotionButton = (addOn: AddOn | null | undefined) =>
    getPredicates(entitlement, PREDICATES.ADD_PROMOTIONS) ? (
      <DropdownItem
        key="add-promotion"
        aria-label="Add promotion to add-on"
        onClick={() =>
          handleSidebarAction({
            sidebarType: SIDEBAR.APPLY_PROMOTION,
            payload: {
              entitlement,
              transactionAccountContext,
              refetch,
              addOn,
            },
            sidebarOptions: {
              isOverlay: true,
              sidebarWidth: '600px',
            },
          })
        }
      >
        Add promotion
      </DropdownItem>
    ) : null;

  const getRowAction = (addOn: AddOn | null | undefined): React.ReactElement => {
    return (
      <DropdownMenu
        testId={'addon-action-menu'}
        position="bottom right"
        triggerType="button"
        triggerButtonProps={{
          appearance: 'subtle',
          className: 'trigger-button',
          iconBefore: <VerticalMoreIcon label="invoice-action" />,
        }}
      >
        <DropdownItemGroup>
          {/* <DropdownItem key={'remove-addon'}>Remove add-on</DropdownItem> */}
          {getAddPromotionButton(addOn)}
        </DropdownItemGroup>
      </DropdownMenu>
    );
  };

  const getRows = () => {
    return (addOnEdges ?? []).map((addOnEdge, index) => {
      const addOn = addOnEdge?.node;
      return {
        key: addOn?.entitlementNumber ?? index.toString(),
        cells: [
          {
            content: addOn?.name ?? <NoDataContentInTable />,
          },
          {
            content: getEntitlementRow(addOn),
          },
          {
            content: addOn?.units?.toString() ?? <NoDataContentInTable />,
          },
          {
            content: addOn?.dateAdded ? <FormattedDate date={addOn.dateAdded} /> : <NoDataContentInTable />,
          },
          {
            content: addOn?.billingStatus ? (
              <EntitlementStatus billingStatus={addOn.billingStatus} provisioningStatus={addOn?.provisioningStatus} />
            ) : (
              <NoDataContentInTable />
            ),
          },
          {
            content: addOn?.dateLastBilled ? <FormattedDate date={addOn.dateLastBilled} /> : <NoDataContentInTable />,
          },
          {
            content: getNextBillingState(addOn),
          },
          {
            content: getRowAction(addOn),
          },
        ],
      };
    });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Name',
        shouldTruncate: true,
        width: 10,
      },
      {
        content: 'Entitlement number',
        width: 10,
      },
      {
        content: 'Units',
        width: 10,
      },
      {
        content: 'Added',
        width: 10,
      },
      {
        content: 'Status',
        width: 10,
      },
      {
        content: 'Last billed',
        width: 10,
      },
      {
        content: 'Next bill',
        width: 10,
      },
      {
        content: '',
        width: 2,
      },
    ];
    return {
      cells,
    };
  };

  const subscribeToAddon =
    getPredicates(entitlement, PREDICATES.SUBSCRIBE_TO_ADD_ONS) && !isSandbox && !isDeletedEntitlement(entitlement) ? (
      <OpenSidebarButton
        sidebar={SIDEBAR.SUBSCRIBE_TO_ADD_ONS}
        sidebarOptions={{
          isOverlay: true,
          sidebarWidth: '600px',
        }}
        payload={{
          entitlement,
          availableAddOns: entitlement.offering?.availableAddOns,
          refetch,
          transactionAccountContext,
        }}
        appearance="link"
        iconBefore={<AddIcon label="Add icon" size="small" />}
        testId="subscribe-to-addon"
        label="Subscribe to additional allocations"
      >
        <span aria-label="Subscribe to additional allocations">Subscribe</span>
      </OpenSidebarButton>
    ) : null;

  return (
    <div className="statuspage-tab" data-testid="statuspage-add-ons">
      <DynamicResultTable
        testId="add-on"
        headingText="Additional allocations"
        head={getHead()}
        rows={getRows()}
        emptyStateProps={emptyStateOrderProp}
        isLoading={loading}
        totalCount={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        isPaginationEnabled={showPagination}
        onRowChange={onRowPerPageChange}
        footer={subscribeToAddon}
      />
    </div>
  );
};

export default AddOnsTab;
